import i18next from 'i18next';
import common from '../../common';
import Dialog from '../../ui/Dialog';
import Text from '../../ui/Text';
import Scene from '../Scene';

export default class DevelopmentScene extends Scene {
	constructor(tile, type = 0) {
		super();

		this._tile = tile;
		this._type = type;
		this._structset = tile.world.structset;

		this._devcost = 0;
		this._purchaseId = 0;
		this._entries = [];

		this._overlay = null;
		this._panel = null;
		this._scrollOffset = new PIXI.Point();
		this._isScrolling = false;
		this._scrollUpHeld = false;
		this._scrollDownHeld = false;
		this._keyUpSignal = null;

		this._separatorBottom = null;
		this._tileImage = tile.children[0].texture;

		this._infoDialog = null;

		this._total = 0;

		this._width = 700;
		this._contentHeight = 0;
	}

	init() {
		const width = this._width;

		if (this._type === 3) {
			this._structset = this._tile.world.upgradeset;
			this._purchaseId = [];
		}

		this.interactive = true;
		this.on('pointerdown', () => {
			P3.audio.playSound('sfx_genericclick');
		});
		this._keyUpSignal = P3.input.signals.keyUp.add(this._onKeyUp, this);

		this._overlay = new PIXI.Sprite(PIXI.Texture.WHITE);
		this._overlay.alpha = 0.4;
		this._overlay.tint = 0x0;
		this._overlay.interactive = true;
		this.addChild(this._overlay);

		this._panel = new PIXI.Container();
		this._panel.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + width * 0.5, 0);
		this._panel.interactive = true;
		this.addChild(this._panel);

		this._panel.background = new PIXI.NineSlicePlane(P3.assets.texture('panel_popup_9slice'), 50, 90, 50, 50);
		this._panel.background.width = width + 60;
		this._panel.background.height = common.STAGE_HEIGHT + 50;
		this._panel.background.position.set(-25, -25);
		this._panel.addChild(this._panel.background);

		this._panel.header = this._createHeader(width);
		this._panel.addChild(this._panel.header);

		this._panel.footer = this._createFooter(width);
		this._panel.footer.y = this._panel.background.height - this._panel.footer.height - 40;
		this._panel.addChild(this._panel.footer);

		this._panel.body = new PIXI.Sprite(PIXI.Texture.WHITE);
		this._panel.body.tint = 0xFFaaaa;
		this._panel.body.width = width;
		this._panel.body.x = 5;
		this._panel.body.height = common.STAGE_HEIGHT - (this._panel.header.height + this._panel.footer.height) - 42;
		this._panel.addChildAt(this._panel.body, 0);

		this._panel.scrollView = new P3.utils.ScrollView();
		this._panel.scrollView.contentBounds = new PIXI.Rectangle();
		this._panel.scrollView.hitArea = new PIXI.Rectangle(0, 0, width + 5, this._panel.body.height);
		this._panel.addChild(this._panel.scrollView);

		this._panel.scrollView.content.mask = new PIXI.Graphics();
		this._panel.scrollView.content.mask.beginFill(0xFF0000);
		this._panel.scrollView.content.mask.drawRect(0, 0, this._panel.body.width + 5, this._panel.body.height);
		this._panel.scrollView.content.mask.endFill();
		this._panel.scrollView.addChild(this._panel.scrollView.content.mask);

		this._panel.scrollBar = this._createScrollBar(this._panel.body.height * 0.78);
		this._panel.scrollBar.position.x = width - 30;
		this._panel.addChild(this._panel.scrollBar);

		this._panel.header.y = this._panel.header.bar.height;
		this._panel.body.y = this._panel.header.y + this._panel.header.height - this._panel.header.bar.height + 10;
		this._panel.scrollView.y = this._panel.body.y;
		this._panel.scrollBar.y = this._panel.body.y + 50;

		const separatorTop = new PIXI.Sprite(P3.assets.texture('divider'));

		separatorTop.tint = 0x8cc541;
		separatorTop.position.set(0, this._panel.body.y - 2);
		separatorTop.width = this._panel.width - 52;
		this._panel.addChild(separatorTop);

		this._separatorBottom = new PIXI.Sprite(P3.assets.texture('divider'));

		this._separatorBottom.tint = 0x8cc541;
		this._separatorBottom.position.set(0, this._panel.footer.y - this._panel.footer.height + 20);
		this._separatorBottom.width = this._panel.width - 52;
		this._panel.addChild(this._separatorBottom);

		this._panel.pivot.x = width * 0.5;

		this.populateContent(width - 50);

		this.updateDevelopmentCost();

		this._panel.scale.set(0.8);
		new TWEEN.Tween(this._panel.scale, this._tweens)
			.to({
				x: 1,
				y: 1,
			}, 300)
			.easing(TWEEN.Easing.Back.Out)
			.start();
	}

	destroy() {
		this._panel.scrollBar.thumb.off('mousedown', this.onScrollBarThumbPointerDown, this);
		this._panel.scrollBar.thumb.off('mouseup', this.onScrollBarThumbPointerUp, this);
		this._panel.scrollBar.thumb.off('touchstart', this.onScrollBarThumbPointerDown, this);
		this._panel.scrollBar.thumb.off('touchend', this.onScrollBarThumbPointerUp, this);
		this._panel.scrollBar.thumb.off('touchendoutside', this.onScrollBarThumbPointerUp, this);
		P3.input.signals.keyUp.detach(this._keyUpSignal);

		super.destroy();
	}

	resize() {
		super.resize();
		this._panel.background.height = common.STAGE_HEIGHT + 50;
		this._panel.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + this._width * 0.5, 0);
		this._panel.footer.y = this._panel.background.height - this._panel.footer.height - 40;
		this._separatorBottom.position.set(0, this._panel.footer.y - this._panel.footer.height + 20);

		// scrollview
		this._panel.body.height = common.STAGE_HEIGHT - (this._panel.header.height + this._panel.footer.height) - 42;
		this._panel.scrollView.content.mask.height = this._panel.body.height;

		const scrollHeight = this._panel.body.height * 0.78;

		this._panel.scrollBar.track.height = scrollHeight;
		this._panel.scrollBar.downButton.y = scrollHeight + 20;

		this._panel.header.y = this._panel.header.bar.height;
		this._panel.body.y = this._panel.header.y + this._panel.header.height - this._panel.header.bar.height + 10;
		this._panel.scrollView.y = this._panel.body.y;
		this._panel.scrollBar.y = this._panel.body.y + 50;

		const bounds = this._panel.scrollView.contentBounds;

		bounds.y = this._contentHeight + bounds.height;
		if (bounds.height - bounds.y > this._panel.body.height) {
			bounds.y += this._panel.body.height;
			this._panel.scrollBar.visible = true;
		} else {
			bounds.y = bounds.height;
			this._panel.scrollBar.visible = false;
		}

		if (this._dialog) {
			this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - this._dialog.height * 0.5);
		}

		this._overlay.x = (common.STAGE_WIDTH - P3.View.width) * 0.5;
		this._overlay.width = P3.View.width;
		this._overlay.height = P3.View.height;
	}

	update() {
		super.update();

		if (this._scrollUpHeld) {
			this._panel.scrollView.content.y += 5;
		} else if (this._scrollDownHeld) {
			this._panel.scrollView.content.y -= 5;
		}

		this._panel.scrollView.update(window.dt);
		this.updateScrollThumb();
	}

	_onKeyUp(event) {
		if (P3.scene.top === this && event.key === 'Backspace') {
			if (this._dialog) {
				this._dialog.onCloseButtonClick();
			} else {
				this.onCloseButtonClick();
			}
		}
	}

	populateContent(width) {
		const structures = [];
		const groups = [];
		const purchased = this._tile.upgrades;

		let spacing = 100;

		for (const i in this._structset) {
			if (Object.hasOwn(this._structset, i)) {
				const t = this._structset[i];

				if ((this._type === 3 && !this._tile.data.structure.validupgrades) || (this._type === 3 && this._tile.data.structure.validupgrades.indexOf(t.id) === -1)) {
					continue;
				}
				if (t.type !== this._type) {
					continue;
				}
				if (t.validTiles && !t.validTiles.includes(this._tile.data.ground.id)) {
					continue;
				}
				if (t.group) {
					if (groups.indexOf(t.group) === -1) {
						structures.push(t);
						groups.push(t.group);
					}
				} else {
					structures.push(t);
				}
			}
		}

		this._entries.length = 0;
		let nextY = 0;

		for (let i = 0; i < structures.length; ++i) {
			const t = structures[i];
			const entry = new PIXI.Container();

			entry.id = t.id;
			this._panel.scrollView.content.addChild(entry);

			entry.image = new PIXI.Sprite(P3.assets.texture(t.images.default));
			entry.image.position.set(20, -5);
			entry.image.scale.set(0.5);

			if (this._type === 3) {
				entry.image.position.set(40, 30);
			}

			if (t.images.default === 'defences_deepwater_pressure_buoy') {
				entry.image.scale.set(0.8);
				entry.image.position.set(35, 20);
			} else if (t.images.default === 'structure_ts_special_04') {
				entry.image.scale.set(0.6);
				entry.image.position.set(15, -40);
			}

			entry.addChild(entry.image);

			let tint = 0x0;

			if (purchased.indexOf(entry.id) > -1) {
				tint = 0x848484;
			}

			entry.nameLabel = new Text(t.description, { font: '30px', fontWeight: 'bold', align: 'left', tint, maxWidth: 350 });
			entry.nameLabel.anchor.set(0, 0);
			entry.nameLabel.position.set(140, 18);
			entry.addChild(entry.nameLabel);

			spacing = Math.max(100, 70 + entry.nameLabel.height);

			const states = new P3.ButtonStates();

			if (purchased.indexOf(entry.id) > -1) {
				states.normal = P3.assets.texture('btn_green_outline_small_out');
				states.over = P3.assets.texture('btn_green_outline_small_over');
				states.icon = P3.assets.texture('icon_tick');
			} else {
				states.normal = P3.assets.texture('btn_green_fill_small_out');
				states.over = P3.assets.texture('btn_green_fill_small_over');
				states.icon = P3.assets.texture('icon_plus');
			}

			entry.buyButton = common.buttonFactory.createButton(states);
			entry.buyButton.position.set(width - entry.buyButton.width, spacing * 0.5);
			entry.buyButton.animate = false;
			entry.buyButton.animateIcon = false;
			entry.buyButton.signals.click.add(this.onEntryBuyButtonClick, this);
			entry.addChild(entry.buyButton);

			entry.buyButton.accessibleTitle = i18next.t('accessibility.button.addToCart');

			// restrict special buildings to target max only
			if (t.special === 'hospital' && common.scenario.targetProgress.hospital >= common.scenario.targets.hospital) {
				entry.buyButton.visible = false;
			}
			if (t.special === 'school' && common.scenario.targetProgress.school >= common.scenario.targets.school) {
				entry.buyButton.visible = false;
			}
			if (purchased.indexOf(entry.id) > -1) {
				entry.buyButton.enabled = false;
			}

			if (purchased.indexOf(entry.id) > -1) {
				entry.costLabel = new Text(`$${common.numberWithCommas(t.cost)}`, { font: '30px', fontWeight: 'bold', align: 'left', tint: 0x848484 });
			} else {
				entry.costLabel = new Text(`$${common.numberWithCommas(t.cost)}`, { font: '30px', fontWeight: 'bold', align: 'left', tint: 0x0 });
			}
			entry.costLabel.anchor.set(1, 0.5);
			entry.costLabel.position.set(entry.buyButton.x - 40, spacing * 0.5);
			entry.addChild(entry.costLabel);

			const iconStripY = entry.nameLabel.y + entry.nameLabel.height + 20;

			entry.defenceIcon = new PIXI.Sprite(P3.assets.texture('icon_contextual_defend'));
			entry.defenceIcon.scale.set(0.5);
			entry.defenceIcon.anchor.set(0.5);
			entry.defenceIcon.position.set(148, iconStripY);
			entry.addChild(entry.defenceIcon);

			entry.defenceValue = new Text(`${t.strength}%`, { font: '28px', align: 'left', tint: 0x0 });
			entry.defenceValue.anchor.set(0, 0.5);
			entry.defenceValue.position.set(entry.defenceIcon.x + 15, iconStripY);
			entry.addChild(entry.defenceValue);

			if (this._type !== 3) {
				entry.populationIcon = new PIXI.Sprite(P3.assets.texture('icon_contextual_develop'));
				entry.populationIcon.anchor.set(0.5);
				entry.populationIcon.scale.set(0.5);
				entry.populationIcon.position.set(Math.max(entry.defenceValue.x + entry.defenceValue.width + 20, 230), iconStripY);
				entry.addChild(entry.populationIcon);

				entry.populationValue = new Text(t.population.toString(), { font: '28px', align: 'left', tint: 0x0 });
				entry.populationValue.anchor.set(0, 0.5);
				entry.populationValue.position.set(entry.populationIcon.x + 15, iconStripY);
				entry.addChild(entry.populationValue);
			}

			entry.infoButton = common.buttonFactory.createButton({ normal: P3.assets.texture('icon_contextual_info') });
			entry.infoButton.scale.set(0.5);
			entry.infoButton.position.set(entry.populationIcon ? (entry.populationValue.x + entry.populationValue.width + 20) : Math.max(entry.defenceValue.x + entry.defenceValue.width + 20, 230), iconStripY);
			entry.infoButton.animate = false;
			entry.infoButton.itemInfo = t.info;
			entry.infoButton.itemDesc = t.description;
			entry.infoButton.signals.click.add(this.onInfoButtonClick, this);
			entry.infoButton.accessibleTitle = i18next.t('accessibility.button.info');
			entry.addChild(entry.infoButton);

			entry.separator = new PIXI.Sprite(P3.assets.texture('divider'));
			entry.separator.alpha = 0.2;
			entry.separator.y = nextY + spacing;
			entry.separator.x = 10;
			width && (entry.separator.width = width - 20);
			this._panel.scrollView.content.addChild(entry.separator);

			this._entries.push(entry);

			if (common.scenario.budget.remaining < t.cost) {
				entry.buyButton.visible = false;
				entry.costLabel.tint = 0xAAAAAA;
			}

			entry.y = nextY;
			nextY += spacing;
		}

		this._contentHeight = -nextY;
		const bounds = this._panel.scrollView.contentBounds;

		bounds.y = this._contentHeight + bounds.height;
		if (bounds.height - bounds.y > this._panel.body.height) {
			bounds.y += this._panel.body.height;
		} else {
			bounds.y = bounds.height;
			this._panel.scrollBar.visible = false;
		}
	}

	updateDevelopmentCost() {
		this._devcost = 0;
		if (this._type === 3) {
			return;
		}

		this._tile.data.structure && (this._devcost += this._tile.data.structure.devcost);
		this._devcost += this._tile.data.ground.devcost;

		this._panel.header.addCostsValueLabel.text = `$${this._devcost}`;
	}

	updateTotalCost() {
		const { buyButton, totalValueLabel, totalItemsLabel } = this._panel.footer;
		let cost = 0;

		if (this._type === 3) {
			for (const p of this._purchaseId) {
				cost += p > 0 ? this._tile.world.getUpgrade(p).cost : 0;
			}
		} else {
			cost = this._purchaseId > 0 ? this._tile.world.getStructure(this._purchaseId).cost : 0;
		}
		if (cost <= 0) {
			buyButton.enabled = false;
			totalValueLabel.text = '';
			totalItemsLabel.text = `${i18next.t('gui.development.footer.total')} (0 ${i18next.t('gui.development.footer.items')})`;
		} else {
			this._total = this._devcost + cost;
			totalValueLabel.text = `$${common.numberWithCommas(this._total)}`;
			if (this._type === 3) {
				const plural = this._purchaseId.length > 1 ? 'gui.development.footer.items' : 'gui.development.footer.item';

				totalItemsLabel.text = `${i18next.t('gui.development.footer.total')} (${this._purchaseId.length} ${i18next.t(plural)})`;
			} else {
				totalItemsLabel.text = `${i18next.t('gui.development.footer.total')} (1 ${i18next.t('gui.development.footer.item')})`;
			}

			buyButton.enabled = true;
		}

		buyButton.accessibleTitle = i18next.t('accessibility.button.buy', { COST: totalValueLabel.text });
	}

	updateScrollThumb() {
		const thumb = this._panel.scrollBar.thumb;
		const thumbRange = this._panel.scrollBar.track.height - thumb.height + 5;

		if (this._isScrolling) {
			const position = this._panel.scrollBar.toLocal(P3.input.touch, common.stage);

			thumb.y = Math.min(thumbRange, Math.max(0, position.y - this._scrollOffset.y));
			this._panel.scrollView.content.y = this._panel.scrollView.contentBounds.height + ((thumb.y / thumbRange)
                * (this._panel.scrollView.contentBounds.y - this._panel.scrollView.contentBounds.height));
		} else {
			const contentRange = this._panel.scrollView.contentBounds.y - this._panel.scrollView.contentBounds.height;
			const f = (this._panel.scrollView.content.y - this._panel.scrollView.contentBounds.height) / contentRange;

			thumb.y = Math.min(thumbRange, Math.max(0, f * thumbRange));
		}
	}

	selectEntry(id) {
		const purchased = this._tile.upgrades;

		if (this._type === 3) {
			const i = this._purchaseId.indexOf(id); // find id in cart

			if (i > -1) {
				this._purchaseId.splice(i, 1);
				P3.audio.playSound('sfx_basket_cancelled_00');
			} else {
				this._purchaseId.push(id); // if found, remove it, else add it.
				P3.audio.playSound('sfx_basket_add_item_00');
			}

			for (let i = 0; i < this._entries.length; ++i) {
				const entry = this._entries[i];
				const states = entry.buyButton.states;

				if (purchased.indexOf(entry.id) > -1) {
					states.normal = P3.assets.texture('btn_green_outline_small_out');
					states.icon = P3.assets.texture('icon_tick');
				} else if (this._purchaseId.indexOf(entry.id) > -1) {
					states.normal = P3.assets.texture('btn_green_outline_small_out');
					states.icon = P3.assets.texture('icon_tick');
				} else {
					states.normal = P3.assets.texture('btn_green_fill_small_out');
					states.icon = P3.assets.texture('icon_plus');
				}
				entry.buyButton.setStates(states);
			}
			P3.audio.playSound('sfx_basket_add_item_00');
			this.updateTotalCost();

			return;
		}

		this._purchaseId = id !== this._purchaseId ? id : 0;
		for (let i = 0; i < this._entries.length; ++i) {
			const entry = this._entries[i];
			const states = entry.buyButton.states;

			if (entry.id === this._purchaseId) {
				states.normal = P3.assets.texture('btn_green_outline_small_out');
				states.icon = P3.assets.texture('icon_tick');
			} else {
				states.normal = P3.assets.texture('btn_green_fill_small_out');
				states.icon = P3.assets.texture('icon_plus');
			}
			entry.buyButton.setStates(states);
		}
		P3.audio.playSound('sfx_basket_add_item_00');
		this.updateTotalCost();
	}

	_createHeader(width) {
		const header = new PIXI.Container();

		header.riskFrame = new PIXI.Sprite(P3.assets.texture('panel_item_risk'));
		header.riskFrame.anchor.set(0);
		header.riskFrame.position.set(7, 0);
		header.addChild(header.riskFrame);

		header.riskFrame.tint = common.riskColours[this._tile.risk];

		header.riskFrame.tileImage = new PIXI.Sprite(this._tileImage);
		header.riskFrame.tileImage.anchor.set(0.5);
		header.riskFrame.tileImage.scale.set(0.425);
		header.riskFrame.tileImage.position.set(header.riskFrame.width * 0.5, header.riskFrame.height * 0.5 + 20);
		header.riskFrame.addChild(header.riskFrame.tileImage);

		if (this._tile.structure > 1) {
			const texture = this._tile._structureSprite.texture;

			header.riskFrame.tileImage.addon = new PIXI.Sprite(texture);
			header.riskFrame.tileImage.addon.anchor.set(0.5, 1);
			header.riskFrame.tileImage.addon.scale.set(0.85);
			header.riskFrame.tileImage.addon.position.set(0, -8);
			header.riskFrame.tileImage.position.y += 30;
			header.riskFrame.tileImage.addChild(header.riskFrame.tileImage.addon);
		}

		header.riskFrame.tileImage.maskG = new PIXI.Graphics();
		header.riskFrame.tileImage.maskG.beginFill(0xff0000, 0.5);
		header.riskFrame.tileImage.maskG.drawRect(0, 0, 125, 152);
		header.riskFrame.tileImage.maskG.endFill();
		header.riskFrame.tileImage.mask = header.riskFrame.tileImage.maskG;
		header.riskFrame.addChild(header.riskFrame.tileImage.maskG);

		header.riskFrame.riskLabel = new Text(`${i18next.t('gui.development.header.risk')} ${parseInt(this._tile.risk) + 1}`, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		header.riskFrame.riskLabel.signals.languageChange.add(() => {
			header.riskFrame.riskLabel.text = `${i18next.t('gui.development.header.risk')} ${parseInt(this._tile.risk) + 1}`;
		});
		header.riskFrame.riskLabel.anchor.set(0.5);
		header.riskFrame.riskLabel.position.set(header.riskFrame.width * 0.5, 25);
		header.riskFrame.addChild(header.riskFrame.riskLabel);

		const t = this._tile.structure > 1
			? this._tile.data.structure : this._tile.data.ground;

		header.tileName = new Text(t.description, { font: '28px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		header.tileName.position.set(header.riskFrame.x + header.riskFrame.width + 10, header.riskFrame.y + 2);
		header.addChild(header.tileName);

		header.riskInfo = this._createRiskInfo();
		header.riskInfo.x = header.tileName.x;
		header.riskInfo.y = header.tileName.y + 30;
		header.addChild(header.riskInfo);

		header.populationInfo = this._createPopInfo();
		header.populationInfo.x = header.riskInfo.x + header.riskInfo.width + 20;
		header.populationInfo.y = header.tileName.y + 30;
		header.addChild(header.populationInfo);

		header.tileInfo = new Text(i18next.t(t.info), { font: '28px', align: 'left', tint: 0x0, maxWidth: width - header.tileName.x });
		header.tileInfo.anchor.set(0, 0);
		header.tileInfo.position.set(header.tileName.x, header.riskInfo.y + 30);
		header.addChild(header.tileInfo);

		header.addCostsLabel = new Text('gui.development.header.additionalCosts', { font: '28px', align: 'left', tint: 0x0 });
		header.addCostsLabel.anchor.set(0, 0);
		header.addCostsLabel.position.set(header.tileName.x, header.tileInfo.y + header.tileInfo.height + 10);
		header.addChild(header.addCostsLabel);

		header.addCostsValueLabel = new Text('$0', { font: '28px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		header.addCostsValueLabel.anchor.set(0);
		header.addChild(header.addCostsValueLabel);

		header.budgetBG = new PIXI.Sprite(P3.assets.texture('budget_underlay'));
		header.budgetBG.scale.set(0.75);
		header.budgetBG.anchor.set(1, 0);
		header.budgetBG.position.set(this._panel.width - 49, -4);
		header.addChild(header.budgetBG);

		header.budgetValue = new Text(`$${common.numberWithCommas(common.scenario.budget.remaining)}`, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		header.budgetValue.anchor.set(0.5);
		header.budgetValue.position.set(header.budgetBG.x - header.budgetBG.width * 0.5 + 10, header.budgetBG.height * 0.5 - 10);
		header.addChild(header.budgetValue);

		header.addCostsValueLabel.position.set(Math.max(width - 135, header.addCostsLabel.x + header.addCostsLabel.width + 5), header.addCostsLabel.y);

		header.bar = this._createHeaderBar();
		header.bar.y = -header.bar.height;
		header.addChild(header.bar);

		return header;
	}

	_createHeaderBar() {
		const container = new PIXI.Container();

		const text = this._type === 0 ? 'gui.development.header.title-defences' : 'gui.development.header.title-structures';
		const label = new Text(text, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });

		label.anchor.set(0, 0.5);
		label.position.set(20, 30);
		container.addChild(label);

		const closeButton = P3.Button.create(P3.assets.texture('btn_close_out'));

		closeButton.position.set(this._panel.background.width - 81, 35);
		closeButton.animate = false;
		closeButton.signals.click.add(this.onCloseButtonClick, this);
		container.addChild(closeButton);

		return container;
	}

	_createPopInfo() {
		const info = new PIXI.Container();

		info.icon = new PIXI.Sprite(P3.assets.texture('icon_contextual_develop'));
		info.icon.scale.set(0.5);
		info.addChild(info.icon);

		const pop = this._tile.structure > 1 ? this._tile.data.structure.population : 0;

		info.label = new Text(pop.toString(), { font: '28px', align: 'left', tint: 0x0 });
		info.label.anchor.set(0, 0.5);
		info.label.position.set(32, 14);
		info.addChild(info.label);

		return info;
	}

	_createRiskInfo() {
		const info = new PIXI.Container();

		info.icon = new PIXI.Sprite(P3.assets.texture('icon_warning'));
		info.icon.position.set(0, 4);
		info.icon.scale.set(0.5);
		info.addChild(info.icon);

		info.label = new Text(`${100 - this._tile.strength}%`, { font: '28px', align: 'left', tint: 0x0 });
		info.label.anchor.set(0, 0.5);
		info.label.position.set(32, 14);
		info.addChild(info.label);

		return info;
	}

	_createFooter(width) {
		const footer = new PIXI.Container();

		footer.buyButton = this._createBuyButton();
		footer.buyButton.position.set(width - footer.buyButton.width * 0.5 - 20, 0);
		footer.buyButton.signals.click.add(this.onBuyButtonClick, this);
		footer.addChild(footer.buyButton);

		footer.totalValueLabel = new Text('', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		footer.totalValueLabel.anchor.set(0, 0.5);
		footer.totalValueLabel.position.set(footer.buyButton.width * -0.5 + 60, 0);
		footer.buyButton.animation.addChild(footer.totalValueLabel);

		footer.buyButton.accessibleTitle = i18next.t('accessibility.button.buy', { COST: footer.totalValueLabel.text });

		footer.totalItemsLabel = new Text(`${i18next.t('gui.development.footer.total')} (0 ${i18next.t('gui.development.footer.items')})`, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		footer.totalItemsLabel.anchor.set(0, 0.5);
		footer.totalItemsLabel.position.set(footer.x + 40, 0);
		footer.addChild(footer.totalItemsLabel);

		return footer;
	}

	_createScrollBar(height) {
		const scroll = new PIXI.Container();

		scroll.upButton = common.buttonFactory.createButton({
			normal: P3.assets.texture('btn_scroll_chevron_out'),
		});

		scroll.upButton.signals.down.add(this.onScrollbarUpButtonPointerDown, this);
		scroll.upButton.signals.click.add(this.onScrollbarUpButtonPointerUp, this);
		scroll.upButton.position.set(7, -20);

		scroll.upButton.accessibleTitle = i18next.t('accessibility.button.scrollUp');

		scroll.thumb = new PIXI.NineSlicePlane(P3.assets.texture('scroll_fg_9slice'), 12, 12, 12, 12);
		scroll.thumb.height = 50;
		scroll.thumb.width = 15;
		scroll.thumb.interactive = true;
		scroll.thumb.buttonMode = true;
		scroll.thumb.on('mousedown', this.onScrollBarThumbPointerDown, this);
		scroll.thumb.on('mouseup', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('mouseupoutside', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('touchstart', this.onScrollBarThumbPointerDown, this);
		scroll.thumb.on('touchend', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('touchendoutside', this.onScrollBarThumbPointerUp, this);

		scroll.track = new PIXI.NineSlicePlane(P3.assets.texture('scroll_bg_9slice'), 12, 12, 12, 12);
		scroll.track.height = height;
		scroll.track.width = scroll.thumb.width;

		scroll.downButton = common.buttonFactory.createButton({
			normal: P3.assets.texture('btn_scroll_chevron_out'),
		});
		scroll.downButton.signals.down.add(this.onScrollbarDownButtonPointerDown, this);
		scroll.downButton.signals.up.add(this.onScrollbarDownButtonPointerUp, this);
		scroll.downButton.scale.y *= -1;
		scroll.downButton.position.set(7, height + 20);

		scroll.downButton.accessibleTitle = i18next.t('accessibility.button.scrollDown');

		scroll.addChild(scroll.upButton);
		scroll.addChild(scroll.track);
		scroll.addChild(scroll.thumb);
		scroll.addChild(scroll.downButton);

		return scroll;
	}

	_createBuyButton() {
		const button = common.buttonFactory.createButton({
			normal: { textureName: 'btn_green_fill_medium_9slice_out', width: 180, height: 64, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_green_fill_medium_9slice_over', width: 180, height: 64, left: 35, top: 35, right: 35, bottom: 35 },
			normalInactive: { textureName: 'btn_gray_fill_medium_9slice_out', width: 180, height: 64, left: 35, top: 35, right: 35, bottom: 35 },
			overInactive: { textureName: 'btn_gray_fill_medium_9slice_over', width: 180, height: 64, left: 35, top: 35, right: 35, bottom: 35 },
		});

		button.enabled = false;
		button.animate = false;

		const icon = new PIXI.Sprite(P3.assets.texture('icon_basket'));

		icon.anchor.set(0.5);
		icon.position.set(button.width * -0.5 + 40, 0);
		button.animation.addChild(icon);

		return button;
	}

	onScrollBarThumbPointerDown(event) {
		this._isScrolling = true;
		this._scrollOffset = event.data.getLocalPosition(this._panel.scrollBar.thumb);
	}

	onScrollBarThumbPointerUp() {
		this._isScrolling = false;
	}

	onScrollbarUpButtonPointerDown() {
		this._scrollUpHeld = true;
	}

	onScrollbarUpButtonPointerUp() {
		this._scrollUpHeld = false;
	}

	onScrollbarDownButtonPointerDown() {
		this._scrollDownHeld = true;
	}

	onScrollbarDownButtonPointerUp() {
		this._scrollDownHeld = false;
	}

	onBuyButtonClick() {
		if (this._type !== 3) {
			common.lastBuild = { id: this._purchaseId, cost: this._total, devcost: this._devcost, special: '' };
		}

		this.signals.next.dispatch(this, this._tile, this._purchaseId, this._total);
	}

	onCancelButtonClick() {
		this.signals.previous.dispatch(this);
	}

	onCloseButtonClick() {
		new TWEEN.Tween(this._panel.scale, this._tweens)
			.to({
				x: 0,
				y: 0,
			}, 200)
			.easing(TWEEN.Easing.Quadratic.Out)
			.onComplete(() => {
				this.signals.previous.dispatch(this);
			})
			.start();
	}

	onEntryBuyButtonClick(button) {
		this.selectEntry(button.parent.id);
	}

	onInfoButtonClick(button) {
		if (this._dialog) {
			this._dialog.destroy({ children: true });
			this._dialog = null;
		}

		this._dialog = new Dialog(400, -1, { _func: this.onDialogDestroy, _scope: this });
		this.addChild(this._dialog);

		this._dialog.setTitleText(button.itemDesc);
		this._dialog.setBodyText(button.itemInfo);

		this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - this._dialog.height * 0.5);

		this._dialog.scale.set(0.8);
		new TWEEN.Tween(this._dialog.scale, this._tweens)
			.to({
				x: 1,
				y: 1,
			}, 300)
			.easing(TWEEN.Easing.Back.Out)
			.start();
	}

	onDialogDestroy() {
		this._dialog = null;
	}

	get tile() {
		return this._tile;
	}

	get type() {
		return this._type;
	}
}
